<script lang="ts">
  import { _ } from 'svelte-i18n'
	import { link, location } from 'svelte-spa-router'
	import { slide } from 'svelte/transition'
  import { getListByFlowItem } from '../../services'
  import { selectedFlow } from '../../store'
  import type { IFlowItem, IFlowListItem } from '../../types'

  import CircleProgress from './components/CircleProgress/CircleProgress.svelte'

  export let item: IFlowItem
  
  let isShowContent: boolean = false
  let items: IFlowListItem[] = []

  $: isComplete = item.percent_viewed === 100
  $: isMobile = (Math.min(window.screen.width, window.screen.height) < 576) as boolean

  const visibleItemsHandler = async () => {
    if (!item.is_enabled) {
      return 
    }

    if (!items.length) {
      items = await getListByFlowItem($selectedFlow.pk, item.pk)
    }

    isShowContent = !isShowContent
  }

  const getVideoDuration = (videoDuration: string) => {
    const [hours, minutes] = videoDuration.split(':');
    return +hours * 60 + (+minutes);
  }
</script>

<div 
  class="accordion" 
  class:disabled={!item.is_enabled}
  style:border-color={!isComplete ? '#B6C8F9' : '#66C383'}
>
  <div 
    class="flex items-center justify-between" 
    class:cursor-pointer={item.is_enabled} 
    on:click={visibleItemsHandler}
  >
    <div class="flex items-center flex-1">
      {#if !item.is_enabled}
        <i class="icon-clock" />
      {:else}
        <i 
          class="icon-chevron-right"
          style:transform={isShowContent ? 'rotate(90deg)' : 'rotate(0)'}  
        />
      {/if}
      
      <div class="title ">{item.title}</div>
    </div>

    {#if !isComplete}
      <CircleProgress progress={item.percent_viewed / 100} disabled={!item.is_enabled} />    
    {:else}
      <i class="icon-ok-circled big" />
    {/if}
  </div>

  {#if isShowContent}
    <div class="content" transition:slide>
      {#each items as item (item.id)}
        <div 
          class="content-item flex items-center justify-between"
          style:padding-bottom={item.videos.length ? '8px' : '24px'}
        >
          <div class="flex items-center">
            <i 
              class:icon-document={item.videos.length > 1 || !item.videos.length} 
              class:icon-play-circle={item.videos.length === 1}
              class:disabled={!item.active}
            />

            {#if item.active}
              <a 
                use:link
                href={`/publicate/${item.id}?prev_url=${$location}`}
                class="content-item__title" 
                class:cut-text-mobile={isMobile}
              >
                {item.title}
              </a>
            {:else}
              <span
                class="content-item__title disabled" 
                class:cut-text-mobile={isMobile}
              >
                {item.title}
              </span>
            {/if}
          </div>
          <div class="flex items-center">
            {#if item.active}
              {#if item.read_time}
                <div class="time">{item.read_time} {$_('min')}</div>
              {/if}

              <div 
                class:pending-color={item.i_view !== 100}
                class:icon-ok-circled={item.i_view === 100} 
              />
            {:else}
              <i class="icon-clock" />
            {/if}
          </div>
        </div>

        {#each item.videos as video, index (index)}
          <div class="content-item video flex items-center justify-between" class:last-video={index === item.videos.length - 1}>
            <div class="flex items-center">
              <i class="icon-play-circle" class:disabled={!item.active} />
              
              {#if item.active}
                <a 
                  use:link
                  href={`/publicate/${item.id}?prev_url=${$location}`}
                  class="content-item__title font-normal" 
                  class:cut-text-mobile={isMobile}
                >
                  {video.video_name}
                </a>
              {:else}
                <span
                  class="content-item__title font-normal disabled" 
                  class:cut-text-mobile={isMobile}
                >
                  {video.video_name}
                </span>
              {/if}
            </div>
            <div class="flex items-center">
              {#if item.active}
                {#if video.video_duration}
                  <div class="time">{getVideoDuration(video.video_duration)} {$_('min')}</div>
                {/if}

                <div 
                  class:pending-color={!video.is_view}
                  class:icon-ok-circled={video.is_view} 
                />
              {:else}
                <i class="icon-clock" />
              {/if}
            </div>
          </div>
        {/each}
      {/each}
    </div>
  {/if}
</div>

<style lang="scss">
  * {
    box-sizing: border-box;
    text-decoration: none;
  }

  i {
    display: flex;
    justify-content: center;
    align-items: center;
    color: #30333D;
  }

  .disabled {
    color: #5A5F6D99;
  }

  ::before {
    margin: 0;
  }

  .cut-text-mobile {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .pending-color {
    background-color: #E0E8F6;
    width: 16px;
    height: 16px;
    border-radius: 50%;
  }
  
  .icon-ok-circled {
    font-size: 16px;
    color: #66C383;

    &.big {
      font-size: 22px;
    }
  }

  .icon-chevron-right {
    cursor: pointer;
    transition: transform .3s;
  }

  a {
    color: #30333D;
  }

  .time {
    color: #5A5F6D99;
    margin-right: 12px;
    font-size: 14px;
    white-space: nowrap;

    @media (max-width: 576px) {
      font-size: 12px;
      margin-right: 8px;
    }
  }

  .icon-clock {
    color: #BDBFC4;
  }

  .accordion {
    margin-top: 20px;
    border: 2px solid;
    width: 100%;
    border-radius: 10px;
    padding: 18px;

    &.disabled {
      border-color: #5A5F6D99 !important;
      border-width: 1px;

      .title {
        color: #5A5F6D99;
      }
    }

    @media (max-width: 576px) {
      padding: 12px;
      margin-top: 12px;
    }
  }

  .content {
    padding-top: 20px;
    font-size: 16px;
    font-weight: 600;
    line-height: 23px;

    @media (max-width: 576px) {
      padding-top: 14px;
    }
  }

  .content-item {
    padding-top: 24px;
    border-top: 1px solid #F3F3F4;

    @media (max-width: 576px) {
      padding: 12px 0 !important;
    }

    &.video {
      border-top: none;
      padding: 8px 0;

      @media (max-width: 576px) {
        padding: 8px 0 !important;
      }
    }

    &.video.last-video {
      padding-bottom: 24px;
    }

    &:last-child {
      padding-bottom: 0 !important;
    }
  }

  .content-item__title {
    max-width: 520px;
    padding: 0 20px;
    font-size: 16px;

    @media (max-width: 576px) {
      font-size: 14px;
      line-height: 20px;
      padding: 0 8px;
    }
  }

  .title {
    font-size: 18px;
    font-weight: 600;
    line-height: 26px;
    margin-left: 26px;
    color: #30333D;

    @media (max-width: 576px) {
      font-size: 16px;
      margin: 0 8px;
      line-height: 23px;
    }
  }

  .icon-chevron-right {
    font-size: 10px;
  }
</style>